import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export const CHART_COLOURS = [
  "#3f7fd6",
  "#23a179",
  "#d18e1e",
  "#9159cf",
  "#cf4a5e",
  "#2b9fb5",
  "#84a832",
  "#c8558c",
];

const prettify = (value) => String(value ?? "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const axisProps = {
  stroke: "currentColor",
  tick: { fontSize: 11, fill: "currentColor" },
  className: "text-muted-foreground",
};

const tooltipStyle = {
  contentStyle: {
    background: "hsl(var(--popover))",
    border: "1px solid hsl(var(--border))",
    borderRadius: 8,
    fontSize: 12,
    color: "hsl(var(--popover-foreground))",
  },
};

/** Renders rows as a table or a bar/line/pie chart. onSelect(row) powers drill-down. */
export const BiChart = ({ type = "table", columns = [], rows = [], valueKeys = [], onSelect = null }) => {
  if (!rows.length) {
    return <p className="text-sm text-muted-foreground" data-testid="bi-chart-empty">No data for this period.</p>;
  }

  const labelKey = columns[0];
  const measures = valueKeys.length ? valueKeys : columns.slice(1);

  if (type === "table") {
    return (
      <div className="overflow-x-auto" data-testid="bi-chart-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              {columns.map((column) => (
                <th key={column} className="text-left py-2 px-3 font-semibold whitespace-nowrap">
                  {prettify(column)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={index}
                onClick={() => onSelect && onSelect(row)}
                className={`border-b ${onSelect ? "cursor-pointer hover:bg-muted/60" : ""}`}
                data-testid={`bi-row-${index}`}
              >
                {columns.map((column) => (
                  <td key={column} className="py-2 px-3 whitespace-nowrap">
                    {row[column] === null || row[column] === undefined || row[column] === "" ? "—" : String(row[column])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === "pie") {
    const measure = measures[0];
    return (
      <div data-testid="bi-chart-pie"><ResponsiveContainer width="100%" height={320}>
        <PieChart>
          <Pie
            data={rows}
            dataKey={measure}
            nameKey={labelKey}
            outerRadius={110}
            label={(entry) => `${entry[labelKey]}: ${entry[measure]}`}
            onClick={(entry) => onSelect && onSelect(entry.payload ?? entry)}
          >
            {rows.map((_, index) => (
              <Cell key={index} fill={CHART_COLOURS[index % CHART_COLOURS.length]} />
            ))}
          </Pie>
          <Tooltip {...tooltipStyle} />
        </PieChart>
      </ResponsiveContainer></div>
    );
  }

  if (type === "line") {
    return (
      <div data-testid="bi-chart-line"><ResponsiveContainer width="100%" height={320}>
        <LineChart data={rows}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey={labelKey} {...axisProps} />
          <YAxis allowDecimals={false} {...axisProps} />
          <Tooltip {...tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {measures.map((measure, index) => (
            <Line
              key={measure}
              type="monotone"
              dataKey={measure}
              name={prettify(measure)}
              stroke={CHART_COLOURS[index % CHART_COLOURS.length]}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5, onClick: (_, payload) => onSelect && onSelect(payload?.payload) }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer></div>
    );
  }

  return (
    <div data-testid="bi-chart-bar"><ResponsiveContainer width="100%" height={320}>
      <BarChart data={rows}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis dataKey={labelKey} {...axisProps} interval={0} angle={rows.length > 6 ? -20 : 0} height={rows.length > 6 ? 60 : 30} textAnchor={rows.length > 6 ? "end" : "middle"} />
        <YAxis allowDecimals={false} {...axisProps} />
        <Tooltip {...tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {measures.map((measure, index) => (
          <Bar
            key={measure}
            dataKey={measure}
            name={prettify(measure)}
            fill={CHART_COLOURS[index % CHART_COLOURS.length]}
            radius={[4, 4, 0, 0]}
            cursor={onSelect ? "pointer" : "default"}
            onClick={(entry) => onSelect && onSelect(entry?.payload ?? entry)}
          />
        ))}
      </BarChart>
    </ResponsiveContainer></div>
  );
};

export default BiChart;
